import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BookOpen, Home, PenLine, Sparkles } from "lucide-react";
import type { Session } from "@supabase/supabase-js";
import {
  BugReporter,
  StatusBanner,
  installGlobalErrorCapture,
  shouldForceReloadOnNavigate,
  useUpdateCheck,
} from "@thinkwithsophia/techne-ui";
import { submitBugReport } from "./lib/bugSink";
import { AuthGate } from "./components/AuthGate";
import { XpDrops } from "./components/XpDrops";
import { Today } from "./screens/Today";
import { Skills } from "./screens/Skills";
import { Log } from "./screens/Log";
import { Journal } from "./screens/Journal";
import { Settings } from "./screens/Settings";
import { onOutboxChange, startOutbox } from "./lib/outbox";
import { supabase } from "./lib/supabase";

const BUILD_ID = (import.meta.env.VITE_BUILD_ID as string | undefined) ?? "dev";
const APP_VERSION = "0.1.0";

const qc = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 15_000, refetchOnWindowFocus: true } },
});

type Tab = "today" | "skills" | "log" | "journal";

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <AuthGate>{(session) => <Shell session={session} />}</AuthGate>
    </QueryClientProvider>
  );
}

function Shell({ session }: { session: Session }) {
  const [tab, setTab] = useState<Tab>("today");
  const [pending, setPending] = useState(0);
  const [settings, setSettings] = useState(false);
  // Deploy survival, part 2: the beacon says a newer build is live. Offer a reload, and force
  // one at the next tab change so a tab can't sit on last week's build indefinitely.
  const updateAvailable = useUpdateCheck(BUILD_ID);
  const go = (next: Tab) => {
    if (shouldForceReloadOnNavigate(updateAvailable, `/${tab}`, `/${next}`)) {
      window.location.reload();
      return;
    }
    setTab(next);
  };

  useEffect(() => {
    const stop = startOutbox();
    const off = onOutboxChange(setPending);
    const uninstall = installGlobalErrorCapture();
    return () => {
      stop();
      off();
      uninstall();
    };
  }, []);

  return (
    <div className="mx-auto min-h-dvh max-w-md">
      <main className="px-4 pb-28 pt-[max(1.25rem,env(safe-area-inset-top))]">
        {tab === "today" && <Today onLog={() => go("log")} userId={session.user.id} />}
        {tab === "skills" && <Skills />}
        {tab === "log" && <Log userId={session.user.id} onDone={() => go("today")} />}
        {tab === "journal" && <Journal />}
      </main>

      {pending > 0 && (
        <div className="fixed inset-x-0 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-30 flex justify-center">
          <span className="rounded-chip bg-surface px-3 py-1 text-xs text-ink-mute shadow-card">
            {pending} entr{pending === 1 ? "y" : "ies"} waiting to sync
          </span>
        </div>
      )}

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-page/90 backdrop-blur">
        <div className="mx-auto grid max-w-md grid-cols-4 pb-[env(safe-area-inset-bottom)]">
          <TabButton
            active={tab === "today"}
            onClick={() => go("today")}
            icon={Home}
            label="Today"
          />
          <TabButton
            active={tab === "skills"}
            onClick={() => go("skills")}
            icon={Sparkles}
            label="Skills"
          />
          <TabButton active={tab === "log"} onClick={() => go("log")} icon={PenLine} label="Log" />
          <TabButton
            active={tab === "journal"}
            onClick={() => go("journal")}
            icon={BookOpen}
            label="Journal"
          />
        </div>
      </nav>

      <button
        aria-label="Settings"
        className="tap fixed right-3 top-[max(0.75rem,env(safe-area-inset-top))] z-30 rounded-chip px-2 py-1 text-xs text-ink-mute"
        onClick={() => setSettings(true)}
      >
        {session.user.email?.split("@")[0]}
      </button>
      <Settings
        open={settings}
        onClose={() => setSettings(false)}
        onSignOut={() => void supabase.auth.signOut()}
      >
        <BugReporter
          subjectId={session.user.id}
          route={`/${tab}`}
          buildId={BUILD_ID}
          version={APP_VERSION}
          submit={submitBugReport}
          extraPayload={{ tab, pending_outbox: pending }}
          triggerLabel="Report a bug"
        />
      </Settings>
      <XpDrops />
      {updateAvailable && (
        <StatusBanner
          tone="info"
          actions={
            <button
              className="tap rounded-chip bg-accent px-3 py-1 text-sm font-semibold text-page"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          }
        >
          A new version of Hearth is ready.
        </StatusBanner>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Home;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`tap flex flex-col items-center gap-0.5 py-2.5 text-[11px] ${active ? "text-accent" : "text-ink-mute"}`}
    >
      <Icon size={20} strokeWidth={active ? 2.4 : 1.8} />
      {label}
    </button>
  );
}
